<template>
  <div>
    <v-container
      class="api-checker-container pa-0"
      fluid
    >
      <v-form
        ref="form"
        v-model="valid"
        class="mt-5 url-form-container"
      >
        <v-row>
          <v-col
            class="d-flex pb-0"
            cols="12"
            sm="12"
          >
            <div class="url-validation-text">
            <p>  This is an interactive implementation of SEMIC’s tool to validate DCAT-AP files
              against their version 2.0.1 specification.
              SEMIC’s specification and associated validation SHACL shapes are available on
              <a href="https://github.com/SEMICeu/DCAT-AP/releases/tag/v2.0.1">SEMIC’s GitHub</a>.
              <br><br>
              You can either drag and drop your DCAT-AP file on the page in the box below, or
              select the file with the “Select Document” button, or – if the file is
              accessible online – paste the URL below. The file must be a valid
              linked data file (application/ld+json, application/rdf+xml, application/n-triples,
              application/trig, text/turtle, text/n3 or text/plain).</p>
            </div>
          </v-col>
          <div class="col mb-5 mt-3">
            <e-c-input
              label="URL"
              v-model="url"
              :validation="validateUrl"
              placeholder="Paste URL here..."
              @keydown.enter.prevent="preventFormSubmit"
            />
          </div>
        </v-row>
        <label class="ecl-form-label">File</label>
        <p class="mt-3">
          Allowed types: application/ld+json, application/rdf+xml, application/n-triples, application/trig, text/turtle, text/n3, text/plain
        </p>
        <v-row>
           <v-col>
          <e-c-shacl-level label="SHACL Shape Model">
            <p class="mb-0">Please select a SHACL shape model for the validation. For more information click <a :href="methodologyShaclLevelsUrl" target="_blank" style="text-decoration: underline;">here</a>.</p>
          </e-c-shacl-level>
           </v-col>
        </v-row>
        <v-row>
          <v-col>
            <dropzone
              v-model="file"
              :label="''"
              :rules="fileRules"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
              class="shacl-validator-button-wrapper"
              cols="12"
          >
            <div class="d-flex">
              <e-c-button
                  :disabled="!valid || loadingStates.editor || loadingStates.validate"
                  :action="handleValidate"
                  :primary="true"
                  class="mr-5"
                  label="Validate"
              />
              <e-c-button
                  :disabled="!valid || loadingStates.editor || loadingStates.validate"
                  :action="handleEdit"
                  label="Edit in Editor"
              />
            </div>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
  </div>
</template>

<script>
import Dropzone from '@/components/Dropzone.vue';

import {
  readFileAsync, getContentTypeByFile, sleep, getDetailedNetworkErrorMsg,
} from '@/utils/helpers';
import ECButton from "@/components/widgets/ECButton";
import ECInput from "@/components/widgets/ECInput";
import ECShaclLevel from '@/components/widgets/ECShaclLevel';

export default {
  name: 'UrlTab',

  components: {ECInput, ECButton, Dropzone, ECShaclLevel},

  data() {
    return {
      type: 'application/rdf+xml',
      items: [
        'application/ld+json',
        'application/rdf+xml',
        'application/n-triples',
        'application/trig',
        'text/turtle',
        'text/n3',
      ],
      valid: false,
      url: '',
      file: undefined,
      dropzone: {
        dragged: false,
      },
      files: [],
      loadingStates: {
        editor: false,
        validate: false,
      },
      urlRules: [
        (value) => {
          // Ignore this rule when there is already a file
          // When visitor provides both file and url then prioritize file
          if (this.file) return true;

          const pattern = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
          return pattern.test(value) || 'Invalid URL (must start with "http://" or "https://").';
        },
      ],
      fileRules: [
        (file) => {
          if (!file) return true;
          const type = this.getContentTypeByFile(file);
          return this.items.includes(type) || `The file '${file.name}' does not point to a valid file of type: ${this.items.join(', ')}`;
        },
      ],
      methodologyShaclLevelsUrl:  window.location.origin + '/mqa/methodology#shacl-levels',
    };
  },

  computed: {
    // Prioritizes file validation in the rare case that the user
    // decides to provide both url and file
    resourceToValidate() {
      return this.file || this.url;
    },
  },

  watch: {
    async file(file) {
      if (file) this.type = this.getContentTypeByFile(file);
      await this.$nextTick();
      this.$refs.form.validate();
    },
  },

  methods: {
    readFileAsync,
    getContentTypeByFile,
    validate() {
      this.$refs.form.validate();
    },
    reset() {
      this.$refs.form.reset();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    preventFormSubmit(event) {
      event.preventDefault();
    },
    async retrieveSchema(res, isFile = false) {
      let schema;
      let type;
      try {
        if (isFile) {
          const file = res;
          type = this.getContentTypeByFile(file);
          if (!this.items.includes(type)) throw new Error(`The file '${file.name}' does not point to a valid file of type: ${this.items.join(', ')}`);
          schema = await this.readFileAsync(file);
        } else {
          const url = res;
          const corsproxy = this.$corsproxyService;

          // Test if given URL points to a valid document
          type = (await corsproxy.head(url)).headers['content-type'];

          // Remove possible charset / boundary from content type
          const index = type.indexOf(';');
          if (index > -1) type = type.substring(0, index);

          if (!this.items.includes(type)) throw new Error(`The URL ${res} does not point to a valid file of type: ${this.items.join(', ')}`);

          const response = await corsproxy.get(url);
          schema = response.data;
        }
      } catch (e) {
        this.$store.dispatch('snackbar/showError', getDetailedNetworkErrorMsg(e) || e);
      }

      return { schema, type };
    },
    // Loads a schema, manages its loading states, and emits an event with the corresponding data
    async loadSchemaAndEmit({ loadingType = 'editor', eventName = 'edit-in-editor' }) {
      const isFile = typeof this.resourceToValidate !== 'string';
      this.loadingStates[loadingType] = true;
      try {
        const { schema, type } = await this.retrieveSchema(this.resourceToValidate, isFile);
        if (schema && type) this.$emit(eventName, schema, type);
      } catch (e) {
        // eslint-disable-next-line no-console
        this.$store.dispatch('snackbar/showError', getDetailedNetworkErrorMsg(e) || e);
      } finally {
        // Sleep a bit before enabling the buttons again due to transition time from
        // switching tabs
        await sleep(1000);
        this.loadingStates[loadingType] = false;
      }
    },
    async handleEdit() {
      await this.loadSchemaAndEmit({ loadingType: 'editor', eventName: 'edit-in-editor' });
    },
    async handleValidate() {
      await this.loadSchemaAndEmit({ loadingType: 'validate', eventName: 'validate-in-editor' });
    },
    handleTextFieldClick() {
      if (this.file) {
        this.$store.dispatch('snackbar/showError', 'If you want to insert a URL, please delete the file in the drag and drop field');
      }
    },
    validateUrl() {
      if (this.url) { // Empty URL should not show an error
        for (let rule of this.urlRules) {
          const result = rule(this.url)
          if (result !== true) {
            return result;
          }
        }
      }
    }
  },
};
</script>

<style lang="scss" scoped>

  .url-form-container {
    // Set a large enough height for the container
    // so that tab switches won't flicker
    //min-height: 800px;
  }

  .url-validation-text {
    white-space: pre-line;
    font-size: 16px;
    font-weight: normal;
    letter-spacing: 0.0125em;
    line-height: 1.5;
    word-break: normal;
  }

  .text-field-wrapper {
    width: 100%;
    div.v-text-field__slot {
      border: 1px solid #aaa !important;
    }
  }
</style>
